import { type BreakoutUser } from '@breakoutlearning/firebase-repository/models/BreakoutUser'
import type { Section } from '@breakoutlearning/firebase-repository/models/Section'
import {
  APPROVED_FREE_PROMOTION_NAME,
  type UserPromotion,
} from '@breakoutlearning/firebase-repository/models/UserPromotion'
import type { UserPromotionRedemption } from '@breakoutlearning/firebase-repository/models/UserPromotionRedemption'
import {
  PromotionType,
  SectionState,
} from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import {
  BreakoutButton,
  type ButtonKind,
} from 'components/design-system/BreakoutButton'
import { BreakoutSelect } from 'components/design-system/BreakoutSelect'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { FormLabel } from 'components/design-system/form/FormLabel'
import { TrashCanIcon } from 'components/icons/TrashCan'
import { useBreakoutUser } from 'hooks/profile'
import { useUserPromotions } from 'hooks/useUserPromotions'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import {
  type ForwardedRef,
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'

export const PromotionSelectionWidget = observer(
  // eslint-disable-next-line mobx/no-anonymous-observer
  forwardRef(function PromotionSelectionWidget(
    {
      kind = 'secondary',
      buttonKind,
      disabled = false,
      selectClassName,
      section,
      /**
       * if we are creating a new section we do not have access to the start date
       * so use the date being defined by the dialog
       */
      sectionStartDate: _sectionStartDate,
      onChange,
      showAdminFreeSectionOption = false,
    }: {
      kind?: 'secondary' | 'tertiary'
      buttonKind?: ButtonKind
      disabled?: boolean
      selectClassName?: string
      section?: Section
      sectionStartDate?: DateTime
      onChange?: (ids: string[]) => void
      /**
       * if showAdminFreeSectionOption is true, an option to create and apply a special one-time
       * promotion for the section will be shown.
       *
       * enable to know whether the option has been selected, the `applyAdminSpecialPromotion` property
       * of the `useUserPromotions` hook should be used. if true apply the special promotion
       */
      showAdminFreeSectionOption?: boolean
    },
    ref: ForwardedRef<
      | {
          selectedUserPromotions: UserPromotion[]
          applyAdminSpecialPromotion: boolean
          user: BreakoutUser
        }
      | undefined
    >
  ) {
    const promotionSelectId = useId()
    const { tt } = useTranslationTyped()

    const {
      user: promotionUser,
      applyAdminSpecialPromotion,
      hasPromotions,
      selectedUserPromotion,
      selectedUserPromotionModel,
      promotionIsActive,
      promotionIsConsumable,
      selectedUserPromotionIds,
      userPromotionOptions,
      userPromotions,
      userPromotionRedemptions,
      userPromotionRedemptionsArray,
      setSelectedUserPromotionIds,
      setSelectedUserPromotion,
      setApplyAdminSpecialPromotion,
    } = useUserPromotions(section)

    // promotion hook returns the impersonating user
    const { canAlwaysApplyAdminApprovedFreePromotion } = useBreakoutUser()

    useEffect(() => {
      if (!ref || !('current' in ref)) return
      ref.current = {
        selectedUserPromotions: userPromotions.filter((p) =>
          selectedUserPromotionIds.includes(p.id)
        ),
        applyAdminSpecialPromotion,
        user: promotionUser,
      }
    }, [
      applyAdminSpecialPromotion,
      ref,
      selectedUserPromotionIds,
      promotionUser,
      userPromotions,
    ])

    const redemptionsForSection = useMemo(() => {
      if (!section) return []
      return userPromotionRedemptionsArray.filter(
        (redemption) => redemption.data.sectionId === section.id
      )
    }, [section, userPromotionRedemptionsArray])

    const approvedFreeIsAppliedToSection = useMemo(() => {
      if (!section) return false
      return userPromotions.some((userPromotion) => {
        return (
          userPromotion.data.promotionName === APPROVED_FREE_PROMOTION_NAME &&
          redemptionsForSection.some(
            (r) => r.data.userPromotionId === userPromotion.id
          )
        )
      })
    }, [redemptionsForSection, section, userPromotions])

    const redeemedOrSelectedPromotionIds = useMemo(() => {
      const promotionIds = new Set<string>()

      for (const promotionId of selectedUserPromotionIds) {
        const promotion = userPromotions.find((p) => p.id === promotionId)
        if (promotion) promotionIds.add(promotion.data.promotionId)
      }

      for (const redemption of redemptionsForSection) {
        promotionIds.add(redemption.data.promotionId)
      }

      if (applyAdminSpecialPromotion)
        promotionIds.add(PromotionType.nFreeSections)

      return promotionIds
    }, [
      applyAdminSpecialPromotion,
      redemptionsForSection,
      selectedUserPromotionIds,
      userPromotions,
    ])

    const promotionIdsAlreadyAppliedToSection = useMemo(
      () => redemptionsForSection.map((r) => r.data.userPromotionId),
      [redemptionsForSection]
    )

    const [selectAdminSpecialPromotion, setSelectAdminSpecialPromotion] =
      useState(false)

    const selectedPromotionId = useMemo(() => {
      if (selectedUserPromotion) {
        const selected = userPromotions.find(
          (p) => p.id === selectedUserPromotion
        )
        if (selected) return selected.data.promotionId
      }
      if (selectAdminSpecialPromotion) return PromotionType.nFreeSections
      return ''
    }, [selectAdminSpecialPromotion, selectedUserPromotion, userPromotions])

    const promotionSelectOptions = useMemo(() => {
      if (!showAdminFreeSectionOption) return userPromotionOptions

      const groupedOptionsForAdmin = [
        {
          groupLabel: tt.instructor_library.admin_promotions(),
          options: [
            {
              label: tt.instructor_library.approved_free_promotion(),
              value: 'adminFreeSectionOption',
            },
          ],
        },
      ]

      if (userPromotionOptions.length)
        groupedOptionsForAdmin.push({
          groupLabel: tt.instructor_library.instructor_promotions(),
          options: userPromotionOptions,
        })

      return groupedOptionsForAdmin
    }, [
      showAdminFreeSectionOption,
      tt.instructor_library,
      userPromotionOptions,
    ])

    /**
     * if start date was passed explicity trust it, else use the value on section if present
     */
    const sectionStartDate = useMemo(() => {
      const startDateFromSection = section?.data.sectionStartDate
        ? DateTime.fromJSDate(section.data.sectionStartDate)
        : undefined
      return _sectionStartDate ?? startDateFromSection
    }, [_sectionStartDate, section?.data.sectionStartDate])

    // if admin special promotion is on dock to be applied, and the proposed section start date changes
    // check here if eligibility has changed, is so un-select the option
    useEffect(() => {
      // if not applied, don't need to check for eligibility
      if (
        !applyAdminSpecialPromotion ||
        canAlwaysApplyAdminApprovedFreePromotion
      )
        return

      // if no start date, can not be eligible
      if (!sectionStartDate) return setApplyAdminSpecialPromotion(false)

      const startedWithinMonth =
        sectionStartDate >= DateTime.now().minus({ months: 1 })

      // if start date is not within the last month, not eligible
      if (!startedWithinMonth) return setApplyAdminSpecialPromotion(false)
    }, [
      applyAdminSpecialPromotion,
      canAlwaysApplyAdminApprovedFreePromotion,
      sectionStartDate,
      setApplyAdminSpecialPromotion,
    ])

    const { promotionCanBeApplied, cantBeAppliedReason } = useMemo(() => {
      // first, check the
      if (selectAdminSpecialPromotion) {
        const selectedAndAppliedPromotionIds = new Set([
          ...selectedUserPromotionIds,
          ...promotionIdsAlreadyAppliedToSection,
        ])

        // can't apply the special promotion if an approved free promotion is already applied
        if (selectedAndAppliedPromotionIds.has(PromotionType.nFreeSections))
          return {
            promotionCanBeApplied: false,
            cantBeAppliedReason:
              tt.instructor_library.promotion_of_type_already_applied(),
          }

        // if user has the super admin privilege, they can always apply the special promotion (if not applied already)
        if (canAlwaysApplyAdminApprovedFreePromotion)
          return {
            promotionCanBeApplied: true,
            cantBeAppliedReason: undefined,
          }

        // if not super admin check the start date, if no start date, don't allow
        if (!sectionStartDate)
          return {
            promotionCanBeApplied: false,
            cantBeAppliedReason: tt.instructor_library.no_section_start_date(),
          }

        const oneMonthAgo = DateTime.now().minus({ months: 1 })

        // start date must be within the last month to apply
        const startedWithinMonth = sectionStartDate >= oneMonthAgo

        if (!startedWithinMonth)
          return {
            promotionCanBeApplied: false,
            cantBeAppliedReason:
              tt.instructor_library.section_started_over_month_ago(),
          }
        return {
          promotionCanBeApplied: true,
          cantBeAppliedReason: undefined,
        }
      }

      // promotion can not be applied if not selected
      if (!selectedUserPromotionModel)
        return {
          promotionCanBeApplied: false,
          cantBeAppliedReason: undefined,
        }

      switch (selectedUserPromotionModel.promotionType) {
        case PromotionType.nFreeSections: {
          const cantBeAppliedReason = (() => {
            if (section?.data.sectionState === SectionState.completed)
              return tt.instructor_library.promotions_disabled_section_completed()
            if (section && section?.userCount > 0)
              return tt.instructor_library.promotions_disabled_tooltip()
            if (!promotionIsActive)
              return tt.instructor_library.promotion_not_active()
            if (!promotionIsConsumable)
              return tt.instructor_library.promotion_not_consumable()
            if (redeemedOrSelectedPromotionIds.has(selectedPromotionId))
              return tt.instructor_library.promotion_of_type_already_applied()
            // we used to disable ALL promotions for sections that use section pass, but now we discriminate by type
            // if (section?.usesSectionPass)
            //   return tt.instructor_library.promotions_disabled_section_pass()
          })()

          return {
            promotionCanBeApplied: !cantBeAppliedReason,
            cantBeAppliedReason,
          }
        }

        // everything not in the PromotionType enum returns as 'unknown', so no need for default case
        case 'unknown': {
          return {
            promotionCanBeApplied: false,
            cantBeAppliedReason:
              tt.instructor_library.promotion_disabled_unsupported_type(),
          }
        }
      }
    }, [
      canAlwaysApplyAdminApprovedFreePromotion,
      promotionIdsAlreadyAppliedToSection,
      promotionIsActive,
      promotionIsConsumable,
      redeemedOrSelectedPromotionIds,
      section,
      sectionStartDate,
      selectAdminSpecialPromotion,
      selectedPromotionId,
      selectedUserPromotionIds,
      selectedUserPromotionModel,
      tt.instructor_library,
    ])

    if (
      !hasPromotions &&
      !showAdminFreeSectionOption &&
      !promotionIdsAlreadyAppliedToSection.length
    )
      return null

    return (
      <div className="flex-1" data-testid="promotion-field">
        <div className="flex flex-col items-start gap-1">
          <FormLabel
            label={tt.instructor_library.promotions_label()}
            for={promotionSelectId}
          />
          <div className="flex w-full flex-row items-center justify-between gap-4">
            <BreakoutSelect
              inputClassName={selectClassName}
              id={promotionSelectId}
              fullWidth
              kind={kind}
              value={
                selectAdminSpecialPromotion
                  ? 'adminFreeSectionOption'
                  : selectedUserPromotion
              }
              loading={!promotionUser.promotionsAndRedemptionsLoaded}
              onChange={(value) => {
                if (value === 'adminFreeSectionOption') {
                  setSelectedUserPromotion(null)
                  return setSelectAdminSpecialPromotion(true)
                }
                setSelectAdminSpecialPromotion(false)
                setSelectedUserPromotion(value)
              }}
              aria-label={tt.instructor_library.select_promotion()}
              className="w-full!"
              options={promotionSelectOptions}
              placeholder={
                promotionSelectOptions.length
                  ? tt.instructor_library.select_promotion()
                  : tt.instructor_library.no_promotions_remaining()
              }
              disabled={
                disabled ||
                !promotionSelectOptions.length ||
                !promotionUser.promotionsAndRedemptionsLoaded
              }
            />
            <BreakoutButton
              size="medium"
              disabled={!promotionCanBeApplied || disabled}
              kind={buttonKind ?? kind}
              className="w-[75px]! max-w-[75px]! min-w-0! rounded-xl!"
              tooltip={cantBeAppliedReason}
              onClick={() => {
                if (!promotionCanBeApplied || disabled) return // same as disabled case

                if (selectAdminSpecialPromotion) {
                  setSelectAdminSpecialPromotion(false)
                  return setApplyAdminSpecialPromotion(true)
                }

                if (selectedUserPromotion) {
                  const newValues = [
                    ...selectedUserPromotionIds,
                    selectedUserPromotion,
                  ]
                  onChange?.(newValues)
                  setSelectedUserPromotionIds(newValues)
                  setSelectedUserPromotion(null)
                }
              }}
            >
              {tt.instructor_library.apply()}
            </BreakoutButton>
          </div>
          <div className="flex max-h-[350px] w-full flex-col gap-2 overflow-auto">
            {applyAdminSpecialPromotion && !approvedFreeIsAppliedToSection && (
              <UserPromotionRow
                userPromotion={'applySpecialAdminPromotion'}
                redemptions={[]}
                onRemove={() => setApplyAdminSpecialPromotion(false)}
                disabled={disabled}
              />
            )}
            {[
              // filter promotions applied from selected, this prevents duplicates from appearing
              // when the redemption has been created but dialog has not yet closed
              ...selectedUserPromotionIds
                .filter(
                  (id) => !promotionIdsAlreadyAppliedToSection.includes(id)
                )
                .map((id) => ({ applied: false, id })),
              ...promotionIdsAlreadyAppliedToSection.map((id) => ({
                applied: true,
                id,
              })),
            ].map(({ applied, id }) => {
              const userPromotion = userPromotions?.find((p) => p.id === id)
              const redemptions = userPromotionRedemptions?.get(id) || []
              if (!userPromotion) return null

              return (
                <UserPromotionRow
                  disabled={disabled}
                  key={id}
                  userPromotion={userPromotion}
                  redemptions={redemptions}
                  onRemove={
                    !applied
                      ? () => {
                          const newValues = selectedUserPromotionIds.filter(
                            (i) => i !== id
                          )
                          onChange?.(newValues)
                          setSelectedUserPromotionIds(newValues)
                        }
                      : undefined
                  }
                />
              )
            })}
          </div>
        </div>
      </div>
    )
  })
)

const UserPromotionRow = observer(function UserPromotionRow({
  userPromotion,
  redemptions,
  onRemove,
  disabled,
}: {
  userPromotion: UserPromotion | 'applySpecialAdminPromotion'
  redemptions: UserPromotionRedemption[]
  disabled: boolean
  onRemove?: () => void
}) {
  const { tt } = useTranslationTyped()

  const {
    promotionName,
    consumableCount,
    isActive,
    consumed,
    expirationDateTime,
  } = useMemo(() => {
    if (typeof userPromotion !== 'string') {
      const { expirationDate, promotionName, consumableCount } =
        userPromotion.data
      const isActive = userPromotion.isActive
      const consumed = redemptions.length
      const expirationDateTime = DateTime.fromJSDate(expirationDate)
      return {
        promotionName,
        consumableCount,
        isActive,
        consumed,
        expirationDateTime,
      }
    }

    return {
      promotionName: APPROVED_FREE_PROMOTION_NAME,
      consumableCount: 1,
      consumed: 1,
      expirationDateTime: DateTime.now().plus({ days: 1 }),
      isActive: true,
    }
  }, [userPromotion, redemptions])

  return (
    <div className="flex flex-row items-center justify-between rounded-2xl bg-surface p-5">
      <div>
        <div
          className={classNames('text-label-small uppercase', {
            'text-core-error': !isActive,
            'text-core-success': isActive,
          })}
        >
          {isActive ? tt.profile.active() : tt.profile.inactive()}
        </div>
        <div className="text-label-medium">{promotionName}</div>
        <div className="text-body-small text-on-surface-var">
          {tt.profile.consumed_x_of_y({
            consumed,
            total: consumableCount,
          })}
        </div>
        <div className="text-body-small text-on-surface-var">
          {tt.profile.expires_at({
            expirationDate: expirationDateTime.toLocaleString(
              DateTime.DATE_FULL
            ),
          })}
        </div>
      </div>
      {onRemove && (
        <BreakoutTooltip
          content={tt.instructor_library.delete_promotion_tooltip()}
          className="max-w-[330px]"
        >
          <div className="text-center">
            <TrashCanIcon
              onClick={!disabled ? onRemove : undefined}
              size={15}
              className={classNames({
                'text-on-surface-disabled': disabled,
                'cursor-pointer': !disabled,
              })}
            />
          </div>
        </BreakoutTooltip>
      )}
    </div>
  )
})
